import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import { buildingUtilityApi } from '../../api/buildingUtilityApi';
import { buildingsApi, Building } from '../../api/buildingsApi';
import { incomeExpenseApi, IeCategory } from '../../api/incomeExpenseApi';
import { BuildingUtilityGroup, UtilityType, UtilityGroupStatus } from '../../types';

export const AddEditUtilityGroup: React.FC = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const buildingIdFromQuery = searchParams.get('buildingId');
  
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    description: '',
    icon: '',
    utilityType: UtilityType.TIME_BASED,
    displayOrder: 1,
    status: UtilityGroupStatus.ACTIVE,
    buildingId: buildingIdFromQuery || '',
    pricing: {},
    usageLimit: {},
    depositRevenueCode: '',
    depositExpenditureCode: '',
    serviceFeeRevenueCode: '',
  });
  const [revenueCategories, setRevenueCategories] = useState<IeCategory[]>([]);
  const [expenditureCategories, setExpenditureCategories] = useState<IeCategory[]>([]);

  const isEdit = !!id;

  useEffect(() => {
    loadBuildings();
    if (isEdit) {
      loadUtilityGroup();
    }
  }, [id]);

  useEffect(() => {
    if (!formData.buildingId) {
      setRevenueCategories([]);
      setExpenditureCategories([]);
      return;
    }
    void loadIncomeExpenseCategories(formData.buildingId);
  }, [formData.buildingId]);

  const loadIncomeExpenseCategories = async (buildingId: string) => {
    try {
      const [revenue, expenditure] = await Promise.all([
        incomeExpenseApi.listRevenueCategories(buildingId),
        incomeExpenseApi.listExpenditureCategories(buildingId),
      ]);
      setRevenueCategories((revenue || []).filter((c) => c.isActive && !c.isSystem));
      setExpenditureCategories((expenditure || []).filter((c) => c.isActive && !c.isSystem));
    } catch {
      setRevenueCategories([]);
      setExpenditureCategories([]);
    }
  };

  const loadBuildings = async () => {
    try {
      const response = await buildingsApi.getBuildings();
      if (response && Array.isArray(response)) {
        setBuildings(response);
        // Auto-select user's building if available and no building specified
        if (!buildingIdFromQuery && user?.buildingId) {
          setFormData(prev => ({ ...prev, buildingId: user.buildingId! }));
        }
      }
    } catch (err: any) {
      setError('Error loading buildings');
    }
  };

  const loadUtilityGroup = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const group = await buildingUtilityApi.getUtilityGroup(id);
      setFormData({
        name: group.name,
        nameEn: group.nameEn || '',
        description: group.description || '',
        icon: group.icon || '',
        utilityType: group.utilityType,
        displayOrder: group.displayOrder,
        status: group.status,
        buildingId: group.buildingId,
        pricing: group.pricing || {},
        usageLimit: group.usageLimit || {},
        depositRevenueCode: group.depositRevenueCode || '',
        depositExpenditureCode: group.depositExpenditureCode || '',
        serviceFeeRevenueCode: group.serviceFeeRevenueCode || '',
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error loading utility group');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.buildingId) {
      setError('Vui lòng chọn tòa nhà');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const ieCodes = {
        depositRevenueCode: formData.depositRevenueCode || undefined,
        depositExpenditureCode: formData.depositExpenditureCode || undefined,
        serviceFeeRevenueCode: formData.serviceFeeRevenueCode || undefined,
      };

      if (isEdit) {
        await buildingUtilityApi.updateUtilityGroup(id!, {
          name: formData.name,
          nameEn: formData.nameEn || undefined,
          description: formData.description || undefined,
          icon: formData.icon || undefined,
          utilityType: formData.utilityType,
          displayOrder: formData.displayOrder,
          status: formData.status,
          pricing: formData.pricing,
          usageLimit: formData.usageLimit,
          ...ieCodes,
        });
      } else {
        await buildingUtilityApi.createUtilityGroup({
          name: formData.name,
          nameEn: formData.nameEn || undefined,
          description: formData.description || undefined,
          icon: formData.icon || undefined,
          utilityType: formData.utilityType,
          displayOrder: formData.displayOrder,
          status: formData.status,
          buildingId: formData.buildingId,
          pricing: formData.pricing,
          usageLimit: formData.usageLimit,
          ...ieCodes,
        });
      }

      navigate('/building-utility-groups');
    } catch (err: any) {
      setError(err.response?.data?.message || `Error ${isEdit ? 'updating' : 'creating'} utility group`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'displayOrder' ? parseInt(value) || 1 : value
    }));
  };

  const getUtilityTypeLabel = (type: UtilityType) => {
    switch (type) {
      case UtilityType.NO_BOOKING:
        return 'Không đặt trước';
      case UtilityType.TIME_BASED:
        return 'Thời gian linh hoạt';
      case UtilityType.FIXED_SLOTS:
        return 'Khung thời gian cố định';
      case UtilityType.NOTIFICATION_ONLY:
        return 'Đăng ký sử dụng';
      default:
        return type;
    }
  };

  if (loading && isEdit) {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <div className="text-center">
          <div className="text-lg text-gray-600">Đang tải thông tin nhóm tiện ích...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8">
      {/* Header */}
      <div className="mb-8">
        <nav className="text-sm text-gray-500 mb-4">
          <span>Tiện ích tòa nhà</span> / 
          <span className="text-gray-900 ml-1">
            {isEdit ? 'Chỉnh sửa nhóm tiện ích' : 'Tạo nhóm tiện ích'}
          </span>
        </nav>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {isEdit ? 'Chỉnh sửa nhóm tiện ích' : 'Tạo nhóm tiện ích mới'}
        </h1>
        <p className="text-gray-600">
          {isEdit ? 'Cập nhật thông tin nhóm tiện ích' : 'Tạo nhóm tiện ích mới cho tòa nhà'}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Building Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tòa nhà <span className="text-red-500">*</span>
            </label>
            <select
              name="buildingId"
              value={formData.buildingId}
              onChange={handleInputChange}
              disabled={isEdit}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              required
            >
              <option value="">-- Chọn tòa nhà --</option>
              {buildings.map((building) => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tên nhóm tiện ích <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ví dụ: Hồ bơi"
                required
              />
            </div>

            {/* English Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tên tiếng Anh
              </label>
              <input
                type="text"
                name="nameEn"
                value={formData.nameEn}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Swimming Pool"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mô tả
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Mô tả về nhóm tiện ích này..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Icon */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Biểu tượng
              </label>
              <input
                type="text"
                name="icon"
                value={formData.icon}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="🏊"
              />
              <p className="text-xs text-gray-500 mt-1">Emoji hoặc ký tự đặc biệt</p>
            </div>

            {/* Utility Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Loại tiện ích <span className="text-red-500">*</span>
              </label>
              <select
                name="utilityType"
                value={formData.utilityType}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                {Object.values(UtilityType).map((type) => (
                  <option key={type} value={type}>
                    {getUtilityTypeLabel(type)}
                  </option>
                ))}
              </select>
            </div>

            {/* Display Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Thứ tự hiển thị
              </label>
              <input
                type="number"
                name="displayOrder"
                value={formData.displayOrder}
                onChange={handleInputChange}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Thu chi mapping */}
          <div className="border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50/80">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Loại thu / chi (Thu chi)</h3>
              <p className="text-xs text-gray-600 mt-1">
                Gắn nhóm tiện ích với mã Loại thu/chi để đồng bộ Hoạt động thu và Thống kê. Phí SHCĐ tại quầy
                dùng <strong>T-SHC001</strong> (khác ký quỹ <strong>T-KQ003</strong>).
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Loại thu ký quỹ (cọc)
                </label>
                <select
                  name="depositRevenueCode"
                  value={formData.depositRevenueCode}
                  onChange={handleInputChange}
                  disabled={!formData.buildingId}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white disabled:bg-gray-100"
                >
                  <option value="">— Chưa chọn —</option>
                  {revenueCategories.map((c) => (
                    <option key={c.id} value={c.code}>
                      {c.code} — {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Loại chi hoàn cọc
                </label>
                <select
                  name="depositExpenditureCode"
                  value={formData.depositExpenditureCode}
                  onChange={handleInputChange}
                  disabled={!formData.buildingId}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white disabled:bg-gray-100"
                >
                  <option value="">— Chưa chọn —</option>
                  {expenditureCategories.map((c) => (
                    <option key={c.id} value={c.code}>
                      {c.code} — {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Loại thu phí sử dụng (tại quầy)
                </label>
                <select
                  name="serviceFeeRevenueCode"
                  value={formData.serviceFeeRevenueCode}
                  onChange={handleInputChange}
                  disabled={!formData.buildingId}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white disabled:bg-gray-100"
                >
                  <option value="">— Chưa chọn —</option>
                  {revenueCategories.map((c) => (
                    <option key={c.id} value={c.code}>
                      {c.code} — {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {formData.buildingId && revenueCategories.length === 0 && (
              <p className="text-xs text-amber-700">
                Chưa có Loại thu cho chung cư này. Vào Thu chi → Loại thu → Tạo mặc định để có T-SHC001, T-KQ003…
              </p>
            )}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Trạng thái
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value={UtilityGroupStatus.ACTIVE}>Hoạt động</option>
              <option value={UtilityGroupStatus.INACTIVE}>Không hoạt động</option>
            </select>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/building-utility-groups')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Đang xử lý...' : (isEdit ? 'Cập nhật' : 'Tạo mới')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 